import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Shield, Cpu, Radio, Search, FileCheck, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/data/services";
import { SectionKicker, SectionHeading, FadeIn } from "@/components/shared/SectionBits";

const ICONS = { shield: Shield, cpu: Cpu, radio: Radio, search: Search, "file-check": FileCheck };

export default function ServiceDetailPage() {
    const { slug } = useParams();
    const svc = SERVICES.find((s) => s.slug === slug);
    if (!svc) return <Navigate to="/services" replace />;
    const Icon = ICONS[svc.icon] || Shield;

    const isCyber = svc.slug === "it-cyber";
    const isHub = Array.isArray(svc.subServices) && svc.subServices.length > 0;

    return (
        <div data-testid={`service-page-${svc.slug}`}>
            {/* Hero */}
            <section className={`relative min-h-[65vh] flex items-end overflow-hidden ${isCyber ? "bg-dr-navy" : ""}`}>
                <div className="absolute inset-0">
                    <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
                    <div className={`absolute inset-0 ${isCyber ? "bg-gradient-to-r from-dr-navy/95 via-dr-navy/85 to-dr-navy/40" : "bg-gradient-to-r from-dr-navy/95 via-dr-navy/75 to-dr-navy/25"}`} />
                </div>
                <div className="relative dr-container w-full py-24">
                    <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> All services
                    </Link>
                    <div className="flex items-center gap-4 mt-8">
                        <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-dr-blue text-dr-navy">
                            <Icon className="w-6 h-6" strokeWidth={2.2} />
                        </span>
                        <p className="text-xs uppercase tracking-[0.32em] text-dr-blue font-medium">{svc.heroTag}</p>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-6 max-w-3xl"
                    >
                        {svc.title}.
                    </motion.h1>
                    <p className="text-white/80 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">{svc.summary}</p>
                </div>
            </section>

            {/* Hub layout: parent page listing sub-services */}
            {isHub ? (
                <section className="bg-dr-grey py-28 md:py-36">
                    <div className="dr-container">
                        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                            <div className="md:col-span-6">
                                <SectionKicker>Areas of practice</SectionKicker>
                                <SectionHeading>Three specialist consultancy streams.</SectionHeading>
                            </div>
                            <div className="md:col-span-6">
                                <p className="text-lg leading-relaxed text-dr-dark/75">
                                    Each stream is led by senior practitioners and delivered discreetly. Choose the
                                    area that best fits your requirement, or speak to a consultant to scope the right
                                    combination.
                                </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {svc.subServices.map((sub, i) => {
                                const SubIcon = ICONS[sub.icon] || Shield;
                                return (
                                    <FadeIn key={sub.slug} delay={i * 0.06}>
                                        <Link
                                            to={`/services/${svc.slug}/${sub.slug}`}
                                            data-testid={`sub-service-card-${sub.slug}`}
                                            className="group block h-full bg-white border border-dr-silver rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(8,41,74,0.12)] transition-[transform,box-shadow] duration-500"
                                        >
                                            <div className="aspect-[4/3] overflow-hidden">
                                                <img
                                                    src={sub.image}
                                                    alt={sub.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                />
                                            </div>
                                            <div className="p-7">
                                                <div className="flex items-center justify-between">
                                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-dr-navy text-white group-hover:bg-dr-blue transition-colors">
                                                        <SubIcon className="w-5 h-5" strokeWidth={2} />
                                                    </span>
                                                    <ArrowUpRight className="w-5 h-5 text-dr-dark/40 group-hover:text-dr-navy group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                                                </div>
                                                <h3 className="font-display text-2xl font-bold text-dr-navy mt-6 leading-tight">{sub.title}</h3>
                                                <p className="text-sm text-dr-dark/70 mt-3 leading-relaxed">{sub.short}</p>
                                            </div>
                                        </Link>
                                    </FadeIn>
                                );
                            })}
                        </div>
                    </div>
                </section>
            ) : (
                /* Standard capabilities grid for services without sub-pages */
                <section className={`${isCyber ? "bg-dr-navy dr-grain" : "bg-dr-grey"} py-28 md:py-36`}>
                    <div className="dr-container">
                        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                            <div className="md:col-span-6">
                                <p className="text-xs tracking-[0.32em] uppercase font-medium mb-4 text-dr-blue">
                                    Capabilities
                                </p>
                                <h2 className={`font-display font-bold text-4xl md:text-5xl leading-tight ${isCyber ? "text-white" : "text-dr-navy"}`}>
                                    What we deliver.
                                </h2>
                            </div>
                            <div className="md:col-span-6">
                                <p className={`text-lg leading-relaxed ${isCyber ? "text-white/70" : "text-dr-dark/75"}`}>
                                    Every capability below is delivered by senior practitioners with the credentials, tooling
                                    and judgment your engagement demands.
                                </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {svc.items.map((item, i) => (
                                <FadeIn key={item.title} delay={i * 0.05}>
                                    <div className={`h-full p-7 rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
                                        isCyber
                                            ? "bg-white/5 border-white/10 hover:bg-white/10"
                                            : "bg-white border-dr-silver hover:shadow-[0_20px_60px_rgba(8,41,74,0.10)]"
                                    }`}>
                                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${isCyber ? "bg-dr-blue/20 text-dr-blue" : "bg-dr-navy/5 text-dr-navy"}`}>
                                            <CheckCircle2 className="w-5 h-5" />
                                        </span>
                                        <h3 className={`font-display text-lg font-semibold mt-5 leading-snug ${isCyber ? "text-white" : "text-dr-navy"}`}>
                                            {item.title}
                                        </h3>
                                        <p className={`text-sm mt-3 leading-relaxed ${isCyber ? "text-white/70" : "text-dr-dark/70"}`}>
                                            {item.desc}
                                        </p>
                                        <Link
                                            to="/contact"
                                            data-testid={`service-item-cta-${svc.slug}-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={`inline-flex items-center gap-1.5 text-sm font-medium mt-5 ${isCyber ? "text-dr-blue" : "text-dr-navy"}`}
                                        >
                                            Learn more <ArrowUpRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA banner */}
            <section className="dr-container py-24">
                <div className="rounded-3xl bg-dr-navy p-10 md:p-16 dr-grain overflow-hidden relative">
                    <div className="grid md:grid-cols-12 gap-10 items-center relative">
                        <div className="md:col-span-8">
                            <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium">Confidential</p>
                            <h3 className="font-display text-white text-3xl md:text-5xl font-bold leading-tight mt-4">
                                Discuss your {svc.title.toLowerCase()} requirement in confidence.
                            </h3>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Link
                                to="/contact"
                                data-testid={`service-cta-${svc.slug}`}
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-dr-blue text-dr-navy font-semibold hover:bg-white transition-colors"
                            >
                                Speak to a consultant
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
